import React, { useEffect, useMemo, useState } from "react";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CFormCheck,
  CNav,
  CNavItem,
  CNavLink,
  CSpinner,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import {
  ClosedPositionsList,
  PortfolioChart,
  PortfolioMetrics,
  StrategiesTable,
} from "@src/components/Portfolio";
import { useExecutions, useStrategies } from "@src/hooks/redux";
import { useClosedPositions } from "@src/hooks/useClosedPositions";
import { useLiveNow } from "@src/hooks/useLiveNow";
import {
  type PortfolioPreset,
  resolvePortfolioRange,
  usePortfolioUrlState,
} from "@src/hooks/usePortfolioUrlState";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type * as T from "@src/types";
import * as portfolioUtils from "@src/utils/portfolioData";

interface Props {}

export const PortfolioPage: React.FC<Props> = () => {
  const { showToast } = useToastActions();
  const { executions: reduxExecutions, setExecutions } = useExecutions();
  const { strategies: reduxStrategies } = useStrategies();

  const [loading, setLoading] = useState(true);

  // URL-backed tab + list filters
  const {
    tab,
    preset,
    from,
    to,
    executionId,
    sync,
    scope,
    setTab,
    setPreset,
    setRange,
    setExecutionId,
    setSync,
    setScope,
  } = usePortfolioUrlState();

  // Chart-only filters (kept local — chart is unchanged for now)
  const [timeRange, setTimeRange] = useState<T.PortfolioTypes.TimeRangeFilter>("1Y");
  const [showAccumulated, setShowAccumulated] = useState(true);
  const [showIndividualStrategies, setShowIndividualStrategies] = useState(true);

  const { nowUtc, refresh: refreshNow } = useLiveNow();

  // Fetch all executions on mount, and again quietly whenever the current time is re-read.
  useEffect(() => {
    const fetchExecutions = async () => {
      try {
        // Paginated fetch — the previous fixed limit silently dropped older
        // executions once an account had >1000 of them.
        const items = await tdApi.getAllExecutions({
          limit: 100,
          sort_by: "id:DESC",
          embed: "live_metrics",
        });
        // Stored metrics of a running execution are only refreshed with its cache; live_metrics are current.
        for (const item of items) {
          item.metrics = item.live_metrics || item.metrics;
        }

        console.log(`[PortfolioPage] Loaded ${items.length} executions`);
        setExecutions(items);
      } catch (error) {
        console.error("[PortfolioPage] Failed to load executions:", error);
        showToast({ message: "Failed to load portfolio data", type: "danger" });
      } finally {
        setLoading(false);
      }
    };

    fetchExecutions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowUtc]);

  const portfolioExecutions = useMemo(
    () =>
      reduxExecutions.filter(
        (exec) => exec.add_to_portfolio === true && (scope === "all" || !exec.end_ts),
      ),
    [reduxExecutions, scope],
  );

  const portfolioExecutionIds = useMemo(
    () => portfolioExecutions.map((e) => e.id),
    [portfolioExecutions],
  );

  // URL-derived `executionId` is untrusted: a hand-edited `?exec=<id>` can
  // reference an execution outside the portfolio scope. Treat anything not
  // in the scope as null and clean up the URL on the next tick.
  const isExecInScope = executionId !== null && portfolioExecutionIds.includes(executionId);
  const safeExecutionId = isExecInScope ? executionId : null;

  useEffect(() => {
    if (executionId !== null && !isExecInScope && portfolioExecutionIds.length > 0) {
      setExecutionId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executionId, isExecInScope, portfolioExecutionIds.length]);

  const toIsNow = preset !== null || to === "";

  // Choosing a preset or "Now" asks for the current time, not the last hourly reading.
  const handlePresetChange = (nextPreset: PortfolioPreset) => {
    refreshNow();
    setPreset(nextPreset);
  };
  const handleRangeChange = (nextFrom: string, nextTo: string | null) => {
    if (nextTo === null) refreshNow();
    setRange(nextFrom, nextTo);
  };
  const range = useMemo(
    () => resolvePortfolioRange(preset, from, to, nowUtc),
    [preset, from, to, nowUtc],
  );

  const closed = useClosedPositions(range.from, range.to, safeExecutionId, portfolioExecutionIds);

  // Executions whose capital counts for the selected dates: the chosen one, or every one running in them.
  const rangeExecutions = useMemo(
    () =>
      safeExecutionId !== null
        ? portfolioExecutions.filter((e) => e.id === safeExecutionId)
        : portfolioExecutions.filter((e) =>
            portfolioUtils.isExecutionInRange(e, range.from, range.to),
          ),
    [portfolioExecutions, safeExecutionId, range],
  );

  const capitalBase = useMemo(
    () => rangeExecutions.reduce((sum, e) => sum + portfolioUtils.getExecutionCapital(e), 0),
    [rangeExecutions],
  );

  // Pure derived state: doing this in useEffect+setState would create a render
  // gap where loading is false but portfolioData is still null, briefly showing
  // the chart's empty state and hiding the metrics strip.
  const portfolioData = useMemo<T.PortfolioTypes.PortfolioData | null>(() => {
    if (portfolioExecutions.length === 0) return null;
    const filteredExecutions = portfolioUtils.filterExecutionsByTimeRange(
      portfolioExecutions,
      timeRange,
    );
    return portfolioUtils.processPortfolioData(filteredExecutions, reduxStrategies);
  }, [portfolioExecutions, reduxStrategies, timeRange]);

  // Lifetime figures ignore the chart's time range.
  const lifetimeMetrics = useMemo(
    () =>
      portfolioExecutions.length > 0
        ? portfolioUtils.processPortfolioData(portfolioExecutions, reduxStrategies).metrics
        : undefined,
    [portfolioExecutions, reduxStrategies],
  );

  const syncedMetrics = useMemo(
    () =>
      portfolioUtils.calculateRangeMetrics(closed.positions, rangeExecutions, range.from, range.to),
    [closed.positions, rangeExecutions, range],
  );

  const stripMetrics = sync ? syncedMetrics : lifetimeMetrics;

  const handleTimeRangeChange = (range: T.PortfolioTypes.TimeRangeFilter) => {
    setTimeRange(range);
  };

  if (loading) {
    return (
      <div className="text-center p-4">
        <CSpinner />
        <p className="mt-2">Loading portfolio data...</p>
      </div>
    );
  }

  // Empty state is keyed off "no portfolio executions at all". The chart's
  // time-range filter (`portfolioData.strategies` post-filter) must not gate
  // the whole page, since the List tab is the default and works on its own
  // date range.
  if (!reduxExecutions.some((exec) => exec.add_to_portfolio === true)) {
    return (
      <CCard>
        <CCardBody>
          <div className="text-center p-4">
            <h4 className="text-body-secondary">No Executions Found</h4>
            <p className="text-body-secondary">
              Start running some executions to see your portfolio performance here.
            </p>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  const chartHasData = portfolioData !== null && portfolioData.strategies.length > 0;

  return (
    <div className="PortfolioPage">
      <div className="PortfolioPage__stripControls d-flex flex-wrap align-items-center gap-3 mb-2 small">
        <CButtonGroup size="sm" className="PortfolioPage__scope">
          <CButton
            color="outline-secondary"
            active={scope === "running"}
            onClick={() => setScope("running")}
          >
            Running
          </CButton>
          <CButton
            color="outline-secondary"
            active={scope === "all"}
            onClick={() => setScope("all")}
          >
            All
          </CButton>
        </CButtonGroup>
        <CFormCheck
          id="portfolioSyncDates"
          className="PortfolioPage__syncDates mb-0"
          label="Sync with selected dates"
          title="On: figures from trades closed in the List tab's dates. Off: lifetime figures."
          checked={sync}
          onChange={(e) => setSync(e.target.checked)}
        />
        {sync && closed.hitCap && (
          <span className="text-warning">
            Figures cover only the most recent trades. Narrow the dates for exact figures.
          </span>
        )}
      </div>

      {/* Portfolio Metrics Summary */}
      {sync && closed.stale ? (
        <div className="PortfolioPage__stripLoading text-center p-3">
          <CSpinner size="sm" />
        </div>
      ) : (
        stripMetrics && <PortfolioMetrics metrics={stripMetrics} />
      )}

      {/* Portfolio Performance: List + Chart tabs */}
      <CCard className="mb-4">
        <CCardHeader className="pb-0">
          <CNav variant="tabs" role="tablist" className="card-header-tabs">
            <CNavItem>
              <CNavLink
                className="PortfolioPage__tab"
                active={tab === "list"}
                onClick={() => setTab("list")}
              >
                List
              </CNavLink>
            </CNavItem>
            <CNavItem>
              <CNavLink
                className="PortfolioPage__tab"
                active={tab === "chart"}
                onClick={() => setTab("chart")}
              >
                Chart
              </CNavLink>
            </CNavItem>
          </CNav>
        </CCardHeader>
        <CCardBody className="p-2">
          <CTabContent>
            <CTabPane role="tabpanel" visible={tab === "list"}>
              <ClosedPositionsList
                executions={portfolioExecutions}
                positions={closed.positions}
                loading={closed.loading}
                hitCap={closed.hitCap}
                capitalBase={capitalBase}
                preset={preset}
                from={range.from}
                to={range.to}
                toIsNow={toIsNow}
                executionId={safeExecutionId}
                onPresetChange={handlePresetChange}
                onRangeChange={handleRangeChange}
                onExecutionChange={setExecutionId}
              />
            </CTabPane>
            <CTabPane role="tabpanel" visible={tab === "chart"}>
              <div className="d-flex justify-content-end mb-2">
                <CButtonGroup size="sm">
                  {(["1M", "3M", "6M", "1Y", "ALL"] as const).map((range) => (
                    <CButton
                      key={range}
                      color="outline-primary"
                      active={timeRange === range}
                      onClick={() => handleTimeRangeChange(range)}
                    >
                      {range}
                    </CButton>
                  ))}
                </CButtonGroup>
              </div>
              <div className="d-flex gap-4 mb-3">
                <CFormCheck
                  id="showAccumulated"
                  label="Show Total PnL"
                  checked={showAccumulated}
                  onChange={(e) => setShowAccumulated(e.target.checked)}
                />
                <CFormCheck
                  id="showIndividualStrategies"
                  label="Show Individual Strategies"
                  checked={showIndividualStrategies}
                  onChange={(e) => setShowIndividualStrategies(e.target.checked)}
                />
              </div>
              {chartHasData ? (
                <PortfolioChart
                  strategies={portfolioData.strategies}
                  portfolioSnapshots={portfolioData.portfolioSnapshots}
                  showAccumulated={showAccumulated}
                  showIndividualStrategies={showIndividualStrategies}
                  height={400}
                />
              ) : (
                <div className="text-center text-body-secondary p-4">
                  No data in the selected time range. Try a wider range.
                </div>
              )}
            </CTabPane>
          </CTabContent>
        </CCardBody>
      </CCard>

      {/* Strategies Table */}
      {chartHasData && (
        <CCard>
          <CCardHeader>
            <h5 className="mb-0">Strategies ({portfolioData.strategies.length})</h5>
          </CCardHeader>
          <CCardBody className="p-2">
            <StrategiesTable strategies={portfolioData.strategies} />
          </CCardBody>
        </CCard>
      )}
    </div>
  );
};
